import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Transition as _Transition, withCtx as _withCtx, createVNode as _createVNode, vModelText as _vModelText, normalizeClass as _normalizeClass, withDirectives as _withDirectives, createTextVNode as _createTextVNode } from "vue"

const _hoisted_1 = { class: "flex items-start justify-start px-4 font-poppins" }
const _hoisted_2 = { class: "w-full p-6 space-y-6 transition-all duration-300" }
const _hoisted_3 = { class: "" }
const _hoisted_4 = { class: "text-sm text-gray-500 mt-1" }
const _hoisted_5 = {
  key: 0,
  class: "bg-red-50 text-red-600 text-sm p-3 rounded-xl animate-shake"
}
const _hoisted_6 = {
  key: 0,
  class: "bg-green-50 text-green-600 text-sm p-3 rounded-xl"
}
const _hoisted_7 = { class: "text-center text-sm text-gray-500" }
const _hoisted_8 = { key: 0 }
const _hoisted_9 = { class: "font-medium text-gray-700" }
const _hoisted_10 = {
  key: 1,
  class: "text-green-600 font-medium animate-pulse"
}
const _hoisted_11 = { class: "flex gap-3" }
const _hoisted_12 = ["disabled"]
const _hoisted_13 = { key: 0 }
const _hoisted_14 = { key: 1 }
const _hoisted_15 = ["disabled"]

import { ref, watch } from 'vue';
import { serviceContainer } from '@/inversify';
import type { OtpClientInterface } from '@/services/integration/clients/otpClientInterface';
import { ApiException } from '@/services/integration/errors/ApiException';
import store from '@/store';
import { HTTPError } from 'ky';


export default /*@__PURE__*/_defineComponent({
  __name: 'OtpVerification',
  props: {
    phoneNumber: {},
    purpose: {},
    active: { type: Boolean }
  },
  emits: ["verified"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const otpClient = serviceContainer.get<OtpClientInterface>('OtpClient');

const otp = ref('');
const timeLeft = ref(0);
const loading = ref(false);
const errorMessage = ref('');
const successMessage = ref('');
const hasSent = ref(false);
const newCode = ref(false);
const timerStarted = ref(false);

let timer: ReturnType<typeof setInterval> | null = null;

/* ---------------------------
   TIMER
---------------------------- */
const startTimer = (seconds: number) => {
  timeLeft.value = seconds;

  if (timer) clearInterval(timer);

  timer = setInterval(() => {
    if (timeLeft.value > 0) {
      timeLeft.value--;
    } else if (timer) {
      clearInterval(timer);
    }
  }, 1000);
};

const formatPhoneNumber = (phone: string): string => {
  if (!phone) return '';

  // Remove spaces and common separators
  const cleaned = phone.replace(/\s|-/g, '');

  // Convert Australian format (04xx → +614xx)
  if (cleaned.startsWith('04')) {
    return `+61${cleaned.substring(1)}`;
  }

  // If already in E.164 format
  if (cleaned.startsWith('+')) {
    return cleaned;
  }

  // Default: add + prefix
  return `+${cleaned}`;
};

/* ---------------------------
   SEND OTP
---------------------------- */
const sendOtp = async () => {
  loading.value = true;
  errorMessage.value = '';
  successMessage.value = '';

  try {
    const data = await otpClient.send(
      formatPhoneNumber(props.phoneNumber),
      props.purpose,
      store.state.cartId,
      'Nexee'
    );

    timerStarted.value = true;
    startTimer(data.expires_in_seconds);
    successMessage.value = 'Verification code sent';
  } catch (error) {
    handleError(error);
  } finally {
    loading.value = false;
  }
};

/* ---------------------------
   VERIFY OTP
---------------------------- */
const verifyOtp = async () => {
  loading.value = true;
  errorMessage.value = '';
  successMessage.value = '';

  try {
    const data = await otpClient.verify(
      formatPhoneNumber(props.phoneNumber),
      otp.value,
      props.purpose
    );

    successMessage.value = 'Phone number verified';
    emit('verified', data);
  } catch (error) {
    handleError(error);
  } finally {
    loading.value = false;
  }
};

/* ---------------------------
   RESEND
---------------------------- */
const resendOtp = async () => {
  newCode.value = false;
  await sendOtp();
};

/* ---------------------------
   ERROR HANDLER
---------------------------- */
const handleError = async (err: unknown) => {
  console.log('OTP error:', err);

  if (err instanceof HTTPError) {
    const errorData = await err.response
      .json()
      .catch(() => null);

    otp.value = '';
    errorMessage.value = errorData?.error?.message;
    newCode.value = errorData?.error?.requires_new_code;

    throw new ApiException({
      message: errorData?.error?.message ?? err.message,
      code: errorData?.error?.code,
      retryable: errorData?.error?.retryable,
      requiresNewCode: errorData?.error?.requires_new_code,
      status: err.response.status,
      details: errorData
    });
  }

  errorMessage.value = 'Unexpected error occurred';

  throw err instanceof Error ? err : new Error('Unknown error');
};

/* ---------------------------
   WATCH ACTIVE STEP
---------------------------- */
watch(
  () => props.active,
  (isActive) => {
    if (isActive && !hasSent.value) {
      hasSent.value = true;
      sendOtp();
    }
  },
  { immediate: true }
);

/* ---------------------------
   RESET IF PHONE CHANGES
---------------------------- */
watch(
  () => props.phoneNumber,
  () => {
    hasSent.value = false;
  }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("p", _hoisted_4, " Code sent to " + _toDisplayString(_ctx.phoneNumber), 1)
      ]),
      _createVNode(_Transition, { name: "fade" }, {
        default: _withCtx(() => [
          (errorMessage.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(errorMessage.value), 1))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }),
      _createVNode(_Transition, { name: "fade" }, {
        default: _withCtx(() => [
          (successMessage.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, _toDisplayString(successMessage.value), 1))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }),
      _withDirectives(_createElementVNode("input", {
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((otp).value = $event)),
        type: "text",
        maxlength: "8",
        inputmode: "numeric",
        placeholder: "••••••",
        class: _normalizeClass(["w-full text-center text-xl tracking-[0.5em] py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200", { 'border-red-400 ring-red-400': errorMessage.value }])
      }, null, 2), [
        [_vModelText, otp.value]
      ]),
      _createElementVNode("div", _hoisted_7, [
        (timeLeft.value > 0)
          ? (_openBlock(), _createElementBlock("span", _hoisted_8, [
              _cache[1] || (_cache[1] = _createTextVNode(" Resend available in ")),
              _createElementVNode("span", _hoisted_9, _toDisplayString(timeLeft.value) + "s ", 1)
            ]))
          : _createCommentVNode("", true),
        (timeLeft.value <= 0 && timerStarted.value)
          ? (_openBlock(), _createElementBlock("span", _hoisted_10, " You can resend the code now "))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_11, [
        _createElementVNode("button", {
          onClick: verifyOtp,
          disabled: otp.value.length < 6 || loading.value || newCode.value,
          class: "flex-1 bg-blue-600 text-white py-2.5 rounded-xl font-medium hover:bg-blue-700 transition disabled:opacity-50 active:scale-95"
        }, [
          (loading.value)
            ? (_openBlock(), _createElementBlock("span", _hoisted_13, "Verifying..."))
            : (_openBlock(), _createElementBlock("span", _hoisted_14, "Verify"))
        ], 8, _hoisted_12),
        _createElementVNode("button", {
          onClick: resendOtp,
          disabled: timeLeft.value > 0 || loading.value,
          class: "flex-1 bg-gray-200 text-gray-700 py-2.5 rounded-xl font-medium hover:bg-gray-300 transition disabled:opacity-50 active:scale-95"
        }, " Resend ", 8, _hoisted_15)
      ])
    ])
  ]))
}
}

})